import { ChatMessage, ActionType } from "../../types";
import { HostType } from "../../types/office";
import { API_BASE_URL } from "../../../config";
import { logger } from "../../utils/logger";
import { isStandaloneMode } from "../identity";

/**
 * Resolve the `application` field the backend records for usage tracking. Standalone (browser, no
 * Office host) reports "Standalone"; otherwise the Office host, defaulting to Outlook if unknown.
 */
function resolveApplication(host: HostType): string {
  if (isStandaloneMode()) return "Standalone";
  return host === "Unknown" ? "Outlook" : String(host);
}

/**
 * API Base URL Configuration
 * 
 * IMPORTANT: The FastAPI backend must have CORS configured to allow requests from:
 * - Development: https://localhost:3000
 * - Production: Your production frontend origin
 * 
 * Example FastAPI CORS configuration (add to your backend main.py or app initialization):
 * ```python
 * from fastapi.middleware.cors import CORSMiddleware
 * 
 * app.add_middleware(
 *     CORSMiddleware,
 *     allow_origins=["https://localhost:3000"],  # Frontend origin
 *     allow_credentials=True,  # Required for Authorization header
 *     allow_methods=["*"],  # Or specify: ["GET", "POST", "OPTIONS"]
 *     allow_headers=["*"],  # Or specify: ["Content-Type", "Authorization"]
 * )
 * ```
 * 
 * If you're still having CORS issues, also ensure:
 * 1. The backend is running and accessible
 * 2. The backend port matches (default FastAPI port is 8000, adjust if different)
 * 3. Try using "http://localhost:8000/api/v1" instead of "http://127.0.0.1/api/v1"
 * 
 * The API_BASE_URL is now configured in config.dev.ts (dev) and config.prod.ts (prod)
 */

/**
 * Auth-related types and interfaces
 */
export interface UserInfo {
  oid: string;
  name: string;
  upn: string;
  tenant: string;
}

export interface LicenseInfo {
  id: string;
  name: string;
  active: boolean;
}

export interface AvailableModel {
  provider: string;
  id: string;
  name: string;
  // Tokens charged = raw tokens × this. 1.0 = baseline model; pricier models 1.5/2/etc.
  usage_multiplier?: number;
}

export interface KnowledgeBase {
  id: string;
  name: string;
}

export interface TokenUsage {
  total_tokens: number;
  used_tokens: number;
  remaining_tokens: number;
  resets_at: string | null; // ISO; next reset across the org's buckets
}

// trialing | active | past_due | paused | canceled | expired | none
export type SubscriptionStatus =
  | "trialing"
  | "active"
  | "past_due"
  | "paused"
  | "canceled"
  | "expired"
  | "none";

export interface Subscription {
  status: SubscriptionStatus;
  plan_name: string | null;
  plan_key: string | null;
  is_trial: boolean;
  current_period_end: string | null; // ISO; next renewal/reset boundary
  trial_end: string | null;          // ISO
  days_left: number | null;          // whole days to trial_end (trialing) / period_end (active)
  cancel_at_period_end: boolean;
}

export interface UserInfoResponse {
  user: UserInfo;
  license: LicenseInfo;
  available_models: AvailableModel[];
  knowledge_bases: KnowledgeBase[];
  available_tokens?: number;  // remaining tokens for the organization (usage meter)
  token_usage?: TokenUsage | null;  // per-org meter: remaining/total/used + reset date
  subscription?: Subscription | null;  // plan + billing cycle for the end-user UX (Phase 3)
}

/**
 * Gets user information from the backend using SSO token
 * @param token - The SSO access token (should be retrieved from getUserIdentity())
 * @returns Promise<UserInfoResponse> - Contains user info, license info, available models, and knowledge bases
 * @throws Error if authentication fails or user is not authorized
 */
export async function getUserInfo(token: string): Promise<UserInfoResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/user-info`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Authentication failed: ${response.status} ${response.statusText}. ${errorText}`);
    }

    const data: UserInfoResponse = await response.json();
    
    // Note: We no longer throw if license is inactive - let the caller handle it
    // This allows the UI to load but show a license warning message

    return data;
  } catch (error) {
    logger.error("Authentication error:", error);
    throw error;
  }
}


/**
 * Maps ActionType to quick action string for Outlook, Word, and PowerPoint
 */
function getQuickAction(actionType: ActionType, host: HostType): string | undefined {
  if (host === "Outlook") {
    switch (actionType) {
      case "summarize":
        return "outlook.summarize";
      case "grammar":
        return "outlook.grammar";
      case "actionItems":
        return "outlook.actionitems";
      case "replyOptions":
        return "outlook.reply";
      case "review":
        return "outlook.template";
      case "professional":
        return "outlook.polish";
      default:
        return undefined;
    }
  }

  if (host === "Word") {
    switch (actionType) {
      case "summarize":
        return "word.summarize";
      case "grammar":
        return "word.grammar";
      default:
        return undefined;
    }
  }

  if (host === "PowerPoint") {
    switch (actionType) {
      case "summarize":
        return "powerpoint.summarize";
      case "notes":
        return "powerpoint.notes";
      case "rewrite":
        return "powerpoint.rewrite";
      default:
        return undefined;
    }
  }

  if (host === "Excel") {
    switch (actionType) {
      case "summarize":
        return "excel.summarize";
      case "pivottable":
        return "excel.pivottable";
      default:
        return undefined;
    }
  }

  return undefined;
}

/**
 * Converts model ID to backend-compatible format
 */
function normalizeModel(model: string): string {
  // Backend expects exact model names, so return as-is
  return model;
}

/**
 * Categorized, user-facing error for a backend/chat request. `retryable` tells the
 * UI whether to offer a Retry affordance.
 */
export type ChatErrorKind =
  | "auth"
  | "insufficient_tokens"
  | "timeout"
  | "network"
  | "server"
  | "unknown";

export interface ChatError {
  kind: ChatErrorKind;
  title: string;
  message: string;
  retryable: boolean;
  statusCode?: number;
}

// Time-to-first-byte budget for a streaming request (ms). Cleared once headers
// arrive, so long-but-healthy streams are never cut off.
const REQUEST_TIMEOUT_MS = 60_000;

const CHAT_ERROR_TITLES: Record<ChatErrorKind, string> = {
  auth: "Sign-in required",
  insufficient_tokens: "Out of tokens",
  timeout: "Request timed out",
  network: "Connection problem",
  server: "Service error",
  unknown: "Something went wrong",
};

function makeChatError(
  kind: ChatErrorKind,
  message: string,
  retryable: boolean,
  statusCode?: number
): ChatError {
  return { kind, title: CHAT_ERROR_TITLES[kind], message, retryable, statusCode };
}

/** Best-effort extraction of a human-readable detail string from an error response. */
async function extractErrorDetail(response: Response): Promise<string> {
  try {
    const body = await response.json();
    const detail = body?.detail ?? body;
    if (typeof detail === "string") return detail;
    if (detail && typeof detail === "object") {
      return detail.error || detail.message || JSON.stringify(detail);
    }
  } catch {
    // Response body was not JSON; fall through to the status-based default.
  }
  return `HTTP ${response.status}`;
}

/** Maps a non-OK HTTP response to a categorized ChatError. */
function classifyHttpError(status: number, detail: string): ChatError {
  if (status === 401 || status === 403) {
    return makeChatError(
      "auth",
      "Your session has expired or you're not authorized. Please sign in again.",
      true,
      status
    );
  }
  if (status === 402) {
    return makeChatError(
      "insufficient_tokens",
      "You've run out of tokens. Please contact support or upgrade your plan to continue.",
      false,
      status
    );
  }
  if (status === 408 || status === 504) {
    return makeChatError("timeout", "The server took too long to respond. Please try again.", true, status);
  }
  if (status === 429) {
    return makeChatError("server", "Too many requests right now. Please wait a moment and try again.", true, status);
  }
  if (status >= 500) {
    return makeChatError(
      "server",
      `The AI service had a problem (HTTP ${status}). Please try again in a moment.`,
      true,
      status
    );
  }
  return makeChatError("unknown", detail || `Request failed (HTTP ${status}).`, true, status);
}

/** Maps a thrown exception (network failure, timeout, auth) to a categorized ChatError. */
function classifyThrownError(error: any, timedOut: boolean): ChatError {
  if (timedOut) {
    return makeChatError("timeout", "The request took too long and was cancelled. Please try again.", true);
  }
  // fetch() rejects with a TypeError on network / CORS / DNS failures.
  if (error instanceof TypeError) {
    return makeChatError("network", "Couldn't reach the AI service. Check your connection and try again.", true);
  }
  const message: string = error?.message || "";
  if (/token|auth|sign[\s-]?in|credential|unauthor/i.test(message)) {
    return makeChatError("auth", "Couldn't authenticate your session. Please sign in again.", true);
  }
  return makeChatError("unknown", message || "An unexpected error occurred. Please try again.", true);
}

/** Public helper for callers that catch their own pre-request errors (e.g. token acquisition). */
export function errorToChatError(error: unknown): ChatError {
  return classifyThrownError(error, false);
}

/**
 * Streams LLM responses from FastAPI backend for chat.
 *
 * Errors are reported via `onError` (categorized + retryable) when provided; otherwise
 * they fall back to surfacing as assistant text (legacy behavior). A user-initiated
 * abort via `signal` is treated as a cancel, not an error.
 */
export async function streamMessage(
  token: string, // Access token from MSAL (should be retrieved from getUserIdentity())
  systemPrompt: string, // System prompt will be added to context, not messages
  chat: ChatMessage[], // Chat history in OpenRouter format
  userContent: string,
  onChunkReceived: (chunk: string) => void,
  onComplete: () => void,
  signal?: AbortSignal,
  model: string = "gpt-4o-mini",
  host: HostType = "Unknown",
  actionType?: ActionType,
  context?: Record<string, any>,
  knowledgeBase?: KnowledgeBase | null,
  onError?: (error: ChatError) => void
) {
  // Report an error via onError when provided, else fall back to surfacing it as
  // assistant text so no caller is left without feedback.
  const reportError = (err: ChatError) => {
    if (onError) {
      onError(err);
    } else {
      onChunkReceived(
        err.kind === "insufficient_tokens"
          ? "Insufficient tokens. Please contact support."
          : "Sorry, there was an error connecting to the AI service."
      );
    }
  };

  // Time out if the server doesn't begin responding within the budget. Forward a
  // user-initiated abort to the same controller so a single signal drives fetch.
  const timeoutController = new AbortController();
  let timedOut = false;
  const timeoutId = setTimeout(() => {
    timedOut = true;
    timeoutController.abort();
  }, REQUEST_TIMEOUT_MS);
  const forwardAbort = () => timeoutController.abort();
  if (signal) {
    if (signal.aborted) {
      timeoutController.abort();
    } else {
      signal.addEventListener("abort", forwardAbort);
    }
  }

  try {
    // Get quick action if applicable
    const quickAction = actionType ? getQuickAction(actionType, host) : undefined;

    // Build messages array - only chat history and user message (no system prompt)
    // System prompt and other context go in the context object
    const chatMessages = chat.map(msg => ({
      content: msg.content,
      role: msg.role
    }));
    const userMessage = { role: "user", content: userContent };

    const messages = [...chatMessages, userMessage];

    // Build request body according to API spec
    const requestBody: any = {
      model: normalizeModel(model),
      application: resolveApplication(host), // "Standalone" in browser; else the Office host
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      messages: messages, // Only chat history and current user message
    };

    // Build context object - include system prompt and any additional context
    const contextObject: Record<string, any> = {
      ...context,
    };

    // Add system prompt to context if provided (backend will combine it)
    if (systemPrompt) {
      contextObject.system_prompt = systemPrompt;
    }

    // Add context to request body
    if (Object.keys(contextObject).length > 0) {
      requestBody.context = contextObject;
    }

    // Add quick action if available
    if (quickAction) {
      requestBody.quickaction = quickAction;
    }

    // Add knowledge base if available
    if (knowledgeBase) {
      requestBody.knowledgebase = {
        id: knowledgeBase.id,
        name: knowledgeBase.name
      };
    }

    const response = await fetch(`${API_BASE_URL}/chat/stream`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
      signal: timeoutController.signal,
    });

    // Headers received — cancel the time-to-first-byte timeout.
    clearTimeout(timeoutId);

    if (!response.ok) {
      const detail = await extractErrorDetail(response);
      reportError(classifyHttpError(response.status, detail));
      onComplete();
      return;
    }

    // Backend streams plain text tokens, not SSE format
    const reader = response.body?.getReader();
    if (!reader) {
      reportError(makeChatError("server", "The server returned an empty response. Please try again.", true));
      onComplete();
      return;
    }

    const decoder = new TextDecoder();
    let accumulatedText = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      accumulatedText += chunk;
      onChunkReceived(accumulatedText);
    }

    onComplete();
  } catch (error: any) {
    // A user-initiated cancel (not a timeout) isn't an error.
    if (signal?.aborted && !timedOut) {
      onComplete();
      return;
    }
    logger.error("Error streaming from FastAPI backend:", error);
    reportError(classifyThrownError(error, timedOut));
    onComplete();
  } finally {
    clearTimeout(timeoutId);
    if (signal) {
      signal.removeEventListener("abort", forwardAbort);
    }
  }
}

/**
 * Formats content for Excel insertion using FastAPI backend
 * Uses Excel-specific API endpoint with selectedCell and sheetName
 */
export async function formatExcelContent(
  token: string, // Access token from MSAL (should be retrieved from getUserIdentity())
  text: string,
  selectedCell: string,
  sheetName: string,
  model: string = "gpt-4o-mini",
  range?: string
): Promise<string> {
  try {
    const requestBody: any = {
      text,
      selectedCell,
      sheetName,
      model: normalizeModel(model),
    };

    // Add context object with range and activeWorksheet
    const context: Record<string, any> = {};
    if (range) {
      context.range = range;
    }
    if (sheetName) {
      context.activeWorksheet = sheetName;
    }
    
    if (Object.keys(context).length > 0) {
      requestBody.context = context;
    }

    const response = await fetch(`${API_BASE_URL}/insert/excel`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: `HTTP ${response.status}` }));
      throw new Error(error.detail || `API error: ${response.status}`);
    }

    // Stream and accumulate formatted content
    const reader = response.body?.getReader();
    if (!reader) throw new Error("Stream reader not available");

    const decoder = new TextDecoder();
    let formattedContent = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      formattedContent += chunk;
    }

    return formattedContent;
  } catch (error) {
    logger.error("Error formatting Excel content:", error);
    throw error;
  }
}

/**
 * Formats content for insertion into Office documents using FastAPI backend
 */
export async function formatContent(
  token: string, // Access token from MSAL (should be retrieved from getUserIdentity())
  text: string,
  application: HostType,
  context?: string,
  model: string = "gpt-4o-mini"
): Promise<string> {
  try {

    const requestBody: any = {
      text,
      application: application === "Unknown" ? "Outlook" : application,
      model: normalizeModel(model),
    };

    if (context) {
      requestBody.context = context;
    }

    const response = await fetch(`${API_BASE_URL}/insert/format`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: `HTTP ${response.status}` }));
      throw new Error(error.detail || `API error: ${response.status}`);
    }

    // Stream and accumulate formatted content
    const reader = response.body?.getReader();
    if (!reader) throw new Error("Stream reader not available");

    const decoder = new TextDecoder();
    let formattedContent = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      formattedContent += chunk;
    }

    // Parse JSON response and extract content field
    try {
      const parsed = JSON.parse(formattedContent);
      return parsed.content || formattedContent; // Fallback to original if content field doesn't exist
    } catch (e) {
      // If parsing fails, return the original content (backward compatibility)
      return formattedContent;
    }
  } catch (error) {
    logger.error("Error formatting content:", error);
    throw error;
  }
}

